import React, { useState, useEffect, useMemo } from "react";
import {
  SearchOutlined,
  FileAddOutlined,
  FolderOpenOutlined,
  EditOutlined,
  DeleteOutlined,
  LikeOutlined,
  DislikeOutlined,
  CheckOutlined,
  CloseOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Layout,
  theme,
  ConfigProvider,
  FloatButton,
  Input,
  Row,
  Form,
  Typography,
  Checkbox,
  Col,
  Card,
  Table,
  Spin,
  Select,
  Breadcrumb,
  Button,
} from "antd";
import CountUp from "react-countup";
import BreadcrumbItem from "antd/es/breadcrumb/BreadcrumbItem";
import menuItems from "../../../config/data/menus.json";
import tableData from "../../../config/data/codes.json";
import FloatButtonComp from "../../../components/FloatButtonComp";
import { useNavigate } from "react-router-dom";
import LayoutHeader from "../../../components/LayoutHeader";
import TableStatus from "../../../config/data/TableViewStatus.json";
import SiderMenuComp from "../../../components/SiderMenuComp";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import ContentTop from "../../../components/ContentTop";
import MainLayout from "../../../components/layouts/MainLayout";

const { Header, Sider, Content } = Layout;
const { Text, Link, Title } = Typography;
const Search = Input;

const rowSelection = {
  onChange: (selectedRowKeys, selectedRows) => {},
  getCheckboxProps: (record) => ({
    disabled: record.name === "Disabled User",
    name: record.name,
  }),
};

const columns = [
  {
    title: "Hook Template",
    dataIndex: "tableName",
  },
  {
    title: "Hook Name",
    dataIndex: "associatedWith",
  },
  {
    title: "Status",
    dataIndex: "associatedWith",
  },
];

const SYA1500 = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [selectedKey, setSelectedKey] = useState("/cst0100");
  const [switchLabel, setSwitchLabel] = useState("Dark Mode");
  const [switchLabelColor, setSwitchLabelColor] = useState("#aeaeae");
  const [menuBG, setMenuBG] = useState("#2F172D");
  const [searchColorBgContainer, setSearchColorBgContainer] =
    useState("#3B2639");
  const [menuTheme, setMenuTheme] = useState("dark");
  const [leftLayoutMargin, setLeftLayoutMargin] = useState(190);
  const [logoDimensions, setLogoDimensions] = useState(100);
  const [menuSearchBarState, setMenuSearchBarState] = useState(false);
  const [menuSearchIconState, setMenuSearchIconState] = useState(true);
  const [switchLabelState, setSwitchLabelState] = useState(false);

  function onSwitchClick() {
    if (switchLabel === "Dark Mode") {
      setSwitchLabel("Light Mode");
      setMenuBG("#fff");
      setSearchColorBgContainer("#f7f8fa");
      setSwitchLabelColor("#8c8f9f");
      setMenuTheme("light");
    } else {
      setSwitchLabelColor("#aeaeae");
      setSwitchLabel("Dark Mode");
      setMenuBG("#2F172D");
      setSearchColorBgContainer("#3B2639");
      setMenuTheme("dark");
    }
  }

  function onMenuSelect(menuItems) {
    // console.log(menuItems.key);
    setSelectedKey(menuItems.key);
    navigate(menuItems.key, { replace: false });
  }

  useEffect(() => {
    if (collapsed === true) {
      setLeftLayoutMargin(71);
      setLogoDimensions(50);
      setMenuSearchBarState(true);
      setMenuSearchIconState(false);
      setSwitchLabelState(true);
    } else {
      setLeftLayoutMargin(190);
      setLogoDimensions(100);
      setMenuSearchBarState(false);
      setMenuSearchIconState(true);
      setSwitchLabelState(false);
    }
  });

  const {
    token: { colorBgContainer },
  } = theme.useToken();
  return (
    <ConfigProvider
      theme={{
        components: {
          Layout: {
            siderBg: menuBG,
          },
          Menu: {
            darkItemBg: menuBG,
            darkSubMenuItemBg: "#241223",
          },
        },
      }}
    >
      <MainLayout
        collapsed={collapsed}
        searchColorBgContainer={searchColorBgContainer}
        theme={menuTheme}
        defaultSelectedKeys={selectedKey}
        items={menuItems}
        onClick={onMenuSelect}
        switchLabel={switchLabel}
        onSwitchClick={onSwitchClick}
        switchLabelColor={switchLabelColor}
        logoDimensions={logoDimensions}
        menuSearchBarState={menuSearchBarState}
        menuSearchIconState={menuSearchIconState}
        switchLabelState={switchLabelState}
        setCollapsed={setCollapsed}
        leftLayoutMargin={leftLayoutMargin}
        colorBgContainer={colorBgContainer}
      >
        <MainPage titleOfPage={"Manage Hooks"} />
      </MainLayout>
    </ConfigProvider>
  );
};

function FloatButtonGroup({
  selectedRowData,
  searchOnClick,
  openOnClick,
  addOnClick,
  openTheme,
  editTheme,
  editOnClick,
}) {
  return (
    <FloatButton.Group
      shape="square"
      style={{
        right: 20,
        bottom: "30%",
      }}
      type="primary"
    >
      <FloatButtonComp
        icon={SearchOutlined}
        tooltip="Search"
        theme="primary"
        onClick={searchOnClick}
      />
      <FloatButtonComp
        icon={FileAddOutlined}
        tooltip="Add"
        theme="primary"
        onClick={addOnClick}
      />
      <FloatButtonComp
        icon={FolderOpenOutlined}
        tooltip="Open"
        theme={openTheme}
        onClick={openOnClick}
      />
      <FloatButtonComp
        icon={EditOutlined}
        tooltip="Edit"
        theme={editTheme}
        onClick={editOnClick}
      />
      <FloatButton icon={<DeleteOutlined />} tooltip={<div>Delete</div>} />
      <FloatButton icon={<LikeOutlined />} tooltip={<div>Approve</div>} />
      <FloatButton icon={<DislikeOutlined />} tooltip={<div>Dissaprove</div>} />
      <FloatButton.BackTop
        visibilityHeight={0}
        tooltip={<div>Back to Top</div>}
      />
    </FloatButton.Group>
  );
}

const iconMap = {
  Authorised: <CheckOutlined />,
  Unauthorised: <CloseOutlined />,
  Disabled: <WarningOutlined />,
};

function AuthSelect(props) {
  const renderOption = (label) => (
    <span>
      {iconMap[label]} {label}
    </span>
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          colorBgContainer: props.colorBgContainer,
          colorText: props.colorText,
        },
      }}
    >
      <Select
        defaultValue={TableStatus[0].value}
        prefixCls="test"
        options={TableStatus.map((status) => ({
          ...status,
          label: renderOption(status.label),
        }))}
        onSelect={props.onSelect}
      />
    </ConfigProvider>
  );
}

function MainPage(props) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchState, setSearchState] = useState("true");
  const [authSelectColour, setAuthSelectColor] = useState("#1bb394");
  const [colorText, setColorText] = useState("black");
  const [openTheme, setOpenTheme] = useState("default");
  const [editTheme, setEditTheme] = useState("default");
  const [tableCount, setTableCount] = useState("10");
  const navigate = useNavigate();

  function onSelect(value) {
    if (value === "authorised") {
      setAuthSelectColor("#1bb394");
    } else if (value === "unauthorised") {
      setAuthSelectColor("#f8ac58");
    } else if (value === "disabled") {
      setAuthSelectColor("#ed5565");
    }
  }

  const handleChange = (value) => {
    console.log(`selected ${value}`);
  };

  function searchOnClick() {
    if (searchState === "true") {
      setSearchState("");
    } else {
      setSearchState("true");
    }
  }

  function openOnClick() {
    if (openTheme === "default") {
    } else {
      navigate("/view-sya1500", { replace: false });
    }
  }

  function handleEditClick() {
    if (editTheme === "default") {
    } else {
      navigate("/edit-sya1500", { replace: false });
    }
  }

  function handleAddClick() {
    navigate("/add-sya1500", { replace: false });
  }

  function selectTableCount(value) {
    setTableCount(value);
  }

  useEffect(() => {
    // Simulate an API call or data fetching
    setTimeout(() => {
      setLoading(false);
      setData(tableData);
    }, 2000);
  }, []);

  useEffect(() => {
    // console.log(selectedRow);
    // setSelectedRow(selectedRow);
  }, [selectedRow]); // This will run every time selectedRow changes

  const onSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedRow(selectedRows[0]);
    setOpenTheme("primary");
    setEditTheme("primary");
  };

  return (
    <div>
      <FloatButtonGroup
        selectedRowData={selectedRow}
        searchOnClick={searchOnClick}
        openOnClick={openOnClick}
        addOnClick={handleAddClick}
        openTheme={openTheme}
        editTheme={editTheme}
        editOnClick={handleEditClick}
      />
      {/* Import Content Top to replace card */}
      <Card
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
          marginBottom: 25,
        }}
      >
        <Col>
          <Row style={{ marginBottom: 0, marginTop: 0 }}>
            <Title level={2} style={{ margin: 0 }}>
              {props.titleOfPage}
            </Title>
          </Row>
          <Breadcrumb style={{ marginBottom: 0 }}>
            <BreadcrumbItem>
              <Link to="/dashboard">Home</Link>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <Text>Hooks</Text>
            </BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Card>

      <div>
        <Card
          title={"Hook LIST"}
          extra={
            <div>
              <Col>
                <AuthSelect
                  colorBgContainer={authSelectColour}
                  colorText={colorText}
                  onSelect={onSelect}
                />
              </Col>
            </div>
          }
          style={{
            boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
            borderRadius: 20,
          }}
        >
          <Spin spinning={loading} size="large">
            <Row style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: "auto" }}>
                <Text style={{ marginRight: 8 }}>Show</Text>
                <Select
                  defaultValue={tableCount}
                  options={[
                    { value: "5", label: "5" },
                    { value: "10", label: "10" },
                    { value: "25", label: "25" },
                    { value: "50", label: "50" },
                  ]}
                  onSelect={selectTableCount}
                />
                <Text style={{ marginLeft: 8 }}>Entries</Text>
              </div>

              <DownloadTableButtons searchState={searchState} />
            </Row>
            <Table
              rowSelection={{
                type: "radio",
                ...rowSelection,
                onChange: onSelectChange,
              }}
              columns={columns}
              dataSource={data}
              bordered
              //   title={() => "Customers List"}
              pagination={{
                position: "bottomCentre",
                pageSize: tableCount,
              }}
            />
          </Spin>
        </Card>
      </div>
      <Row gutter={16}></Row>
    </div>
  );
}

function DownloadTableButtons(props) {
  const colSpan = 7;

  const csvData = [
    ["Name", "Client No.", "Branch"],
    ...tableData.map((item) => [item.name, item.clientno, item.branch]),
  ];

  return (
    <ConfigProvider
      theme={{
        components: {},
        token: {},
      }}
    >
      <Row style={{ marginLeft: "auto", marginBottom: "auto" }}>
        {" "}
        <div hidden={props.searchState} style={{ marginRight: 50 }}>
          <Search
            placeholder="Search by Name or client ID..."
            onSearch={console.log("search")}
            prefix={<SearchOutlined />}
            style={{
              width: 400,
            }}
          />
        </div>
        <Row>
          {" "}
          <Col style={{ width: "25%" }}>
            <Button
              style={{
                padding: 5,
                borderTopLeftRadius: 10,
                borderBottomLeftRadius: 10,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                backgroundColor: "white",
              }}
            >
              <CSVLink data={csvData} filename={"CustomerTable.csv"}>
                CSV
              </CSVLink>
            </Button>
          </Col>
          <Col style={{ width: "28%" }}>
            <Button
              style={{
                padding: 5,
                borderRadius: 0,
                backgroundColor: "white",
              }}
            >
              Excel
            </Button>
          </Col>
          <Col style={{ width: "22%" }}>
            <Button
              style={{
                padding: 5,
                borderRadius: 0,
                backgroundColor: "white",
              }}
            >
              PDF
            </Button>
          </Col>
          <Col style={{ width: "25%" }}>
            <Button
              style={{
                padding: 5,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderTopRightRadius: 10,
                borderBottomRightRadius: 10,
                backgroundColor: "white",
              }}
            >
              Print
            </Button>
          </Col>
        </Row>
      </Row>
    </ConfigProvider>
  );
}

export default SYA1500;
